/**
 * V1.5.8.31 — Superadmin data-heal panel.
 *
 * One-click UI for the /api/superadmin/heal/candidate-workspace/* endpoints.
 * User's request today: "prod bug where public-apply candidates have no
 * workspace_id — build the button so I don't have to touch the console."
 *
 * Flow:
 *   1. Preview button → shows counts (total orphans, healable, unhealable)
 *   2. Apply Heal button → runs the heal, shows healed count + any per-row
 *      failures (usually stale job_id references).
 *   3. Orphan list button → paginated dump of the last 50 orphans for
 *      manual triage if apply couldn't heal them.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ArrowsClockwise, Warning, CheckCircle, Bandaids, MagnifyingGlass } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { resolveAiResponse } from "@/hooks/useAiJob";

export default function SuperAdminDataHeal() {
    const [preview, setPreview] = useState(null);
    const [healResult, setHealResult] = useState(null);
    const [orphans, setOrphans] = useState(null);
    const [busy, setBusy] = useState(null);
    const [diagQuery, setDiagQuery] = useState("");
    const [diagResult, setDiagResult] = useState(null);
    const [invisible, setInvisible] = useState(null);
    const [invisibleHealed, setInvisibleHealed] = useState(null);
    const [dupes, setDupes] = useState(null);
    // V1.5.8.43 — Bulk Re-parse
    const [bulkLimit, setBulkLimit] = useState(50);
    const [bulkAllWs, setBulkAllWs] = useState(false);
    const [bulkForce, setBulkForce] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    // V1.5.8.44 — Legacy resume_url migration
    const [urlPreview, setUrlPreview] = useState(null);
    const [urlApplyResult, setUrlApplyResult] = useState(null);
    // V1.5.8.45 — Orphan candidate users (a + c)
    const [orphanUsers, setOrphanUsers] = useState(null);
    const [orphanApplyResult, setOrphanApplyResult] = useState(null);

    const previewOrphanUsers = async () => {
        setBusy("orphan-preview");
        try {
            const r = await api.get("/superadmin/heal/orphan-users/preview");
            setOrphanUsers(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };
    const applyOrphanUsers = async () => {
        if (!window.confirm(
            `Reconcile ${orphanUsers?.orphan_count - (orphanUsers?.breakdown?.test_data || 0)} orphan users? ` +
            `Test-data accounts are SKIPPED. For each remaining user we either link to an existing candidates row (by email) OR create a fresh Team-Pool row. Safe & idempotent.`
        )) return;
        setBusy("orphan-apply");
        try {
            const r = await api.post("/superadmin/heal/orphan-users/apply", {
                limit: 500,
                include_sources: ["resume_studio", "public_apply_no_link", "self_signup_no_apply"],
            });
            setOrphanApplyResult(r.data);
            const t = r.data.totals;
            toast.success(`Linked ${t.linked_to_existing} · Created ${t.created_new_row} · Skipped ${t.skipped_test_data}${t.errors ? ` · ${t.errors} errors` : ""}`);
            previewOrphanUsers();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const previewResumeUrls = async () => {
        setBusy("url-preview");
        try {
            const r = await api.get("/superadmin/heal/resume-url-migration/preview");
            setUrlPreview(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };
    const applyResumeUrls = async () => {
        if (!window.confirm(
            `Migrate ${urlPreview?.malformed_count || 0} legacy resume_url values? ` +
            `Rows with a real R2 file are rewritten to the canonical /api/storage/cv/{cid} path. ` +
            `Rows with no file behind them get resume_url cleared (safe — the UI already checks presence).`
        )) return;
        setBusy("url-apply");
        try {
            const r = await api.post("/superadmin/heal/resume-url-migration/apply");
            setUrlApplyResult(r.data);
            const t = r.data.totals;
            toast.success(`Normalised ${t.normalised} · cleared ${t.cleared}${t.errors ? ` · ${t.errors} errors` : ""}`);
            previewResumeUrls();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const runBulkReparse = async () => {
        if (!window.confirm(
            `Run AI Re-parse on up to ${bulkLimit} candidate${bulkLimit > 1 ? "s" : ""}` +
            `${bulkAllWs ? " across ALL workspaces" : " in your workspace"}` +
            `${bulkForce ? " (FORCE — will re-run even on already-filled rows)" : ""}?` +
            `\n\nEach row = 1 Claude Sonnet 4.5 call. Fill-blanks-only unless FORCE is on.`
        )) return;
        setBusy("bulk-reparse");
        setBulkResult(null);
        try {
            const raw = await api.post("/candidates/bulk/reparse-resume", {
                limit: bulkLimit,
                all_workspaces: bulkAllWs,
                force: bulkForce,
            });
            // V1.9.4 — endpoint returns {job_id, aggregate_id}. We poll
            // the aggregate progress endpoint (which merges every 50-row
            // chunk's totals) instead of the per-chunk job. This way the
            // UI shows real-time cumulative progress even while the
            // chained continuation jobs are still churning through the
            // remaining candidates.
            const aggregateId = raw.data?.aggregate_id;
            if (!aggregateId) {
                // Old shape (cache hit / non-migrated) — fall back.
                const data = await resolveAiResponse(raw.data);
                setBulkResult(data);
            } else {
                // Poll every 3 s until complete === true or 20 min ceiling.
                for (let i = 0; i < 400; i++) {
                    const prog = await api.get(`/candidates/bulk/reparse-progress/${aggregateId}`);
                    setBulkResult(prog.data);
                    if (prog.data?.complete) break;
                    await new Promise((r) => setTimeout(r, 3000));
                }
            }
            const t = (bulkResult?.totals) || {};
            toast.success(
                `Scanned ${t.scanned || 0} · filled ${t.fields_filled || 0} field${(t.fields_filled || 0) === 1 ? "" : "s"} across ${t.filled_any || 0} candidate${(t.filled_any || 0) === 1 ? "" : "s"}${t.errors ? ` · ${t.errors} error${t.errors === 1 ? "" : "s"}` : ""}`
            );
        } catch (e) { toast.error(formatApiError(e) || e?.message || "Bulk re-parse failed."); }
        finally { setBusy(null); }
    };

    const runDiag = async () => {
        if (!diagQuery.trim() || diagQuery.trim().length < 2) {
            toast.error("Enter at least 2 characters.");
            return;
        }
        setBusy("diag");
        try {
            const r = await api.get(`/superadmin/heal/candidate-diagnostics?q=${encodeURIComponent(diagQuery.trim())}`);
            setDiagResult(r.data);
        } catch (e) {
            toast.error(formatApiError(e) || "Lookup failed.");
        } finally {
            setBusy(null);
        }
    };

    const previewInvisible = async () => {
        setBusy("inv-preview");
        try {
            const r = await api.get("/superadmin/heal/invisible/preview");
            setInvisible(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };
    const applyInvisible = async () => {
        if (!window.confirm(`Move all ${invisible?.invisible_count} invisible candidates to team pool?`)) return;
        setBusy("inv-apply");
        try {
            const r = await api.post("/superadmin/heal/invisible/apply");
            setInvisibleHealed(r.data);
            toast.success(`Healed ${r.data.healed} candidates — now visible to all recruiters.`);
            previewInvisible();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };
    const loadDupes = async () => {
        setBusy("dupes");
        try {
            const r = await api.get("/superadmin/heal/duplicates?limit=50");
            setDupes(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };
    const mergeGroup = async (keepCid, archiveCids) => {
        if (!window.confirm(`Keep ${keepCid.slice(0, 8)}… and archive ${archiveCids.length} other row(s)? Archived rows are soft-deleted (recoverable) but hidden from reports/lists.`)) return;
        setBusy("merge");
        try {
            const r = await api.post("/superadmin/heal/merge-duplicates", {
                keep_cid: keepCid,
                archive_cids: archiveCids,
            });
            toast.success(`Archived ${r.data.archived_count}. Backfilled fields: ${(r.data.fields_backfilled || []).join(", ") || "(none needed)"}.`);
            loadDupes();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const run = async (kind, method, url) => {
        setBusy(kind);
        try {
            const r = await (method === "POST"
                ? api.post(url)
                : api.get(url));
            if (kind === "preview")  setPreview(r.data);
            if (kind === "apply")    { setHealResult(r.data); toast.success(`Healed ${r.data.healed} candidates.`); }
            if (kind === "orphans")  setOrphans(r.data);
        } catch (e) {
            toast.error(formatApiError(e) || "Request failed.");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div
            data-testid="superadmin-data-heal-page"
            className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1200px] mx-auto"
        >
            <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 inline-flex items-center gap-1.5">
                    <ShieldCheck size={12} weight="bold" /> Superadmin · Data heal
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Orphan candidates.
                </h1>
                <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                    Public-apply candidates historically landed without a{" "}
                    <code className="text-[10px] bg-[#F5F5F5] px-1.5 py-0.5">workspace_id</code>,
                    which made them invisible to tenant-scoped reads and caused
                    "No details available" on their 360 drawer. Run Preview to
                    see how many docs are orphaned, then Apply Heal to fix them
                    (idempotent — safe to run multiple times).
                </p>
            </div>

            {/* Action row */}
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
                <button
                    type="button"
                    onClick={() => run("preview", "GET", "/superadmin/heal/candidate-workspace/preview")}
                    disabled={busy !== null}
                    data-testid="heal-preview-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider border border-[#E5E5E5] bg-white text-ink hover:border-ink disabled:opacity-50"
                >
                    <MagnifyingGlass size={14} weight="bold" />
                    {busy === "preview" ? "Loading…" : "1. Preview counts"}
                </button>
                <button
                    type="button"
                    onClick={() => {
                        if (!window.confirm("Apply workspace_id backfill to all orphan candidates? This is safe and idempotent.")) return;
                        run("apply", "POST", "/superadmin/heal/candidate-workspace/apply");
                    }}
                    disabled={busy !== null || !preview || preview.orphans_total === 0}
                    data-testid="heal-apply-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider bg-ink text-white hover:opacity-90 disabled:opacity-40"
                >
                    <Bandaids size={14} weight="bold" />
                    {busy === "apply" ? "Healing…" : "2. Apply heal"}
                </button>
                <button
                    type="button"
                    onClick={() => run("orphans", "GET", "/superadmin/heal/candidate-workspace/orphans?limit=50")}
                    disabled={busy !== null}
                    data-testid="heal-orphans-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider border border-[#E5E5E5] bg-white text-[#525252] hover:border-ink hover:text-ink disabled:opacity-50"
                >
                    <ArrowsClockwise size={14} weight="bold" />
                    {busy === "orphans" ? "Loading…" : "List remaining orphans"}
                </button>
            </div>

            {/* Preview result */}
            {preview && (
                <section
                    data-testid="heal-preview-result"
                    className="bg-white border border-[#E5E5E5] p-4 sm:p-6 mb-5"
                >
                    <div className="flex items-center gap-2 mb-4">
                        <MagnifyingGlass size={16} weight="bold" className="text-cobalt" />
                        <h2 className="font-heading text-lg font-black tracking-tight text-ink">Preview counts</h2>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Stat label="Total orphans" value={preview.orphans_total} tone={preview.orphans_total > 0 ? "warn" : "ok"} />
                        <Stat label="Healable" value={preview.healable} tone="ok" />
                        <Stat label="No job_id" value={preview.unhealable_no_job_id} tone="muted" />
                        <Stat label="Job w/o workspace" value={preview.unhealable_job_missing_or_no_ws} tone="muted" />
                    </div>
                    {preview.orphans_total === 0 && (
                        <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#059669]">
                            <CheckCircle size={14} weight="fill" /> No orphans — all candidates already have workspace_id set.
                        </p>
                    )}
                </section>
            )}

            {/* Apply result */}
            {healResult && (
                <section
                    data-testid="heal-apply-result"
                    className="bg-white border border-[#059669] border-l-4 p-4 sm:p-6 mb-5"
                >
                    <div className="flex items-center gap-2 mb-4">
                        <CheckCircle size={16} weight="fill" className="text-[#059669]" />
                        <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                            Heal complete — {healResult.healed} candidates fixed
                        </h2>
                    </div>
                    {healResult.failed_count > 0 && (
                        <>
                            <p className="text-xs text-[#B45309] font-bold mb-2 inline-flex items-center gap-1.5">
                                <Warning size={12} weight="fill" /> {healResult.failed_count} candidate(s) could not be healed:
                            </p>
                            <ul className="text-xs space-y-1 max-h-64 overflow-y-auto border border-[#F5F5F5] p-2">
                                {(healResult.failed || []).map((f, i) => (
                                    <li key={i} className="flex items-start gap-2 p-1">
                                        <code className="text-[10px] text-[#525252]">{(f.id || "").slice(0, 8)}…</code>
                                        <span className="text-ink truncate flex-1">{f.name || f.email || "(no name)"}</span>
                                        <span className="text-[10px] text-[#B45309]">{f.reason}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </section>
            )}

            {/* V1.5.8.35 — Bulk repair for invisible candidates */}
            <section
                data-testid="heal-bulk-invisible"
                className="bg-white border border-[#E5E5E5] p-4 sm:p-6 mb-5"
            >
                <div className="flex items-center gap-2 mb-3">
                    <Warning size={16} weight="fill" className="text-[#0EA5E9]" />
                    <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                        Invisible candidates (bulk repair)
                    </h2>
                </div>
                <p className="text-xs text-[#525252] mb-3 max-w-2xl">
                    Candidates with no owner AND not in team pool are invisible to
                    every recruiter (only superadmin sees them). One click flips
                    them into the team pool so the whole workspace can pick up.
                </p>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={previewInvisible}
                        disabled={busy !== null}
                        data-testid="heal-inv-preview-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-black uppercase tracking-wider border border-[#E5E5E5] bg-white hover:border-ink"
                    >
                        {busy === "inv-preview" ? "Loading…" : "Preview invisible count"}
                    </button>
                    <button
                        type="button"
                        onClick={applyInvisible}
                        disabled={busy !== null || !invisible || invisible.invisible_count === 0}
                        data-testid="heal-inv-apply-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-black uppercase tracking-wider bg-[#0EA5E9] text-white hover:opacity-90 disabled:opacity-40"
                    >
                        {busy === "inv-apply" ? "Fixing…" : "Move all to team pool"}
                    </button>
                </div>
                {invisible && (
                    <p className="mt-3 text-xs font-bold text-ink">
                        {invisible.invisible_count === 0
                            ? "✓ No invisible candidates — everyone is reachable."
                            : `⚠ ${invisible.invisible_count} candidate(s) are currently invisible.`}
                    </p>
                )}
                {invisibleHealed && (
                    <p className="mt-2 text-xs font-bold text-[#059669]">
                        ✓ Healed {invisibleHealed.healed} candidates.
                    </p>
                )}
            </section>

            {/* V1.5.8.35 — Duplicate detection & merge */}
            <section
                data-testid="heal-duplicates"
                className="bg-white border border-[#E5E5E5] p-4 sm:p-6 mb-5"
            >
                <div className="flex items-center gap-2 mb-3">
                    <ArrowsClockwise size={16} weight="bold" className="text-[#DC2626]" />
                    <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                        Duplicate candidate rows
                    </h2>
                </div>
                <p className="text-xs text-[#525252] mb-3 max-w-2xl">
                    Groups of rows sharing the same linked user OR same email.
                    Pick the row to keep; the rest are soft-archived (recoverable)
                    and their non-empty fields are merged into the winner first.
                </p>
                <button
                    type="button"
                    onClick={loadDupes}
                    disabled={busy !== null}
                    data-testid="heal-dupes-load-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-black uppercase tracking-wider border border-[#E5E5E5] bg-white hover:border-ink"
                >
                    {busy === "dupes" ? "Scanning…" : "Scan for duplicates"}
                </button>
                {dupes && (
                    <div className="mt-3">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2">
                            {dupes.count} duplicate group(s)
                        </p>
                        {(dupes.groups || []).map((g, gi) => (
                            <div key={gi} className="border border-[#F5F5F5] p-3 mb-2 bg-[#FAFAFA]">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2">
                                    Group by {g.key_type} · {g.rows.length} rows
                                </p>
                                {g.rows.map((r) => (
                                    <div key={r.id} className="flex items-start gap-3 py-1.5 border-b border-[#F5F5F5] last:border-0">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-black text-ink truncate">{r.name || "(no name)"}</p>
                                            <p className="text-[10px] text-[#525252] truncate">
                                                {r.email || "—"} · {r.source} · job {r.job_id ? "✓" : "✗"} · pool {r.is_team_pool ? "✓" : "✗"} · title {r.current_title ? "✓" : "✗"}
                                            </p>
                                            <p className="text-[9px] text-[#A3A3A3] mt-0.5">{r.id}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => mergeGroup(
                                                r.id,
                                                g.rows.filter((x) => x.id !== r.id).map((x) => x.id),
                                            )}
                                            disabled={busy !== null}
                                            data-testid={`heal-dupes-keep-${r.id}`}
                                            className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[9px] font-black uppercase tracking-wider bg-ink text-white hover:opacity-90 disabled:opacity-40"
                                        >
                                            Keep this
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* V1.5.8.32 — Candidate diagnostics. Type a name/email/phone
                fragment, get every matching row with the exact state the
                UI reads. Used to answer "why is Devendra's drawer blank
                even though the workspace_id heal already ran?". */}
            <section
                data-testid="heal-diag-section"
                className="bg-white border border-[#E5E5E5] p-4 sm:p-6 mb-5"
            >
                <div className="flex items-center gap-2 mb-3">
                    <MagnifyingGlass size={16} weight="bold" className="text-[#7E22CE]" />
                    <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                        Candidate diagnostics
                    </h2>
                </div>
                <p className="text-xs text-[#525252] mb-3 max-w-2xl">
                    Type a name / email / phone fragment. Shows every matching
                    candidate row + the exact fields that drive the list card,
                    the person 360 drawer, and the workspace scoping — so you
                    can pinpoint which field is missing.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                    <input
                        type="text"
                        value={diagQuery}
                        onChange={(e) => setDiagQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && runDiag()}
                        placeholder="e.g. Devendra or swatimalik@example.com"
                        data-testid="heal-diag-input"
                        className="flex-1 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                    />
                    <button
                        type="button"
                        onClick={runDiag}
                        disabled={busy !== null}
                        data-testid="heal-diag-btn"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-wider bg-[#7E22CE] text-white hover:opacity-90 disabled:opacity-40"
                    >
                        {busy === "diag" ? "Looking up…" : "Diagnose"}
                    </button>
                </div>
                {diagResult && (
                    <div data-testid="heal-diag-result" className="mt-2">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2">
                            {diagResult.count} match(es) for "{diagResult.query}"
                        </p>
                        {(diagResult.candidates || []).map((c) => {
                            const problems = [];
                            if (!c.workspace_id) problems.push("workspace_id missing");
                            if (!c.job_id) problems.push("job_id missing");
                            if (!c.job_exists && c.job_id) problems.push("job_id points to a deleted job");
                            if (c.candidate_user_id && c.candidate_user_id_valid === false)
                                problems.push("candidate_user_id references a deleted user");
                            if (c.person_query_matches === 0) problems.push("person 360 would return 403");
                            if (!c.owner_id && !c.is_team_pool)
                                problems.push("no owner and not in team pool — invisible to recruiters");
                            if (!c.current_title) problems.push("current_title empty");
                            if (!c.email) problems.push("email empty");
                            return (
                                <div key={c.id}
                                     data-testid={`heal-diag-card-${c.id}`}
                                     className="border border-[#E5E5E5] p-3 mb-2 bg-[#FAFAFA]">
                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                        <div>
                                            <p className="font-heading text-base font-black text-ink">
                                                {c.name || "(no name)"}
                                            </p>
                                            <p className="text-xs text-[#525252]">
                                                {c.email || "(no email)"} · {c.phone || "(no phone)"}
                                            </p>
                                            <p className="text-[10px] text-[#A3A3A3] mt-1">ID: {c.id}</p>
                                        </div>
                                        <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 ${
                                            problems.length === 0
                                                ? "bg-[#ECFDF5] text-[#059669]"
                                                : "bg-[#FEE2E2] text-[#B91C1C]"
                                        }`}>
                                            {problems.length === 0 ? "healthy" : `${problems.length} issue(s)`}
                                        </span>
                                    </div>
                                    {problems.length > 0 && (
                                        <ul className="mt-2 text-[11px] text-[#B91C1C] space-y-0.5">
                                            {problems.map((p) => (
                                                <li key={p}>• {p}</li>
                                            ))}
                                        </ul>
                                    )}
                                    <details className="mt-2">
                                        <summary className="text-[10px] uppercase tracking-wider font-bold text-[#525252] cursor-pointer">
                                            Raw fields
                                        </summary>
                                        <pre className="text-[10px] text-[#525252] bg-white p-2 mt-1 overflow-x-auto">
{JSON.stringify(c, null, 2)}
                                        </pre>
                                    </details>

                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <TestPerson360Btn cid={c.id} />
                                        <FixRowBtn cid={c.id} onDone={runDiag} />
                                        {!c.current_title && (
                                            <RepairTitleBtn cid={c.id} onDone={runDiag} />
                                        )}
                                        {!c.is_team_pool && !c.owner_id && (
                                            <RepairTeamPoolBtn cid={c.id} onDone={runDiag} />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Orphan list */}
            {orphans && (
                <section
                    data-testid="heal-orphans-result"
                    className="bg-white border border-[#E5E5E5] p-4 sm:p-6 mb-5"
                >
                    <div className="flex items-center gap-2 mb-4">
                        <ArrowsClockwise size={16} weight="bold" className="text-[#525252]" />
                        <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                            Remaining orphans ({orphans.count})
                        </h2>
                    </div>
                    {orphans.count === 0 ? (
                        <p className="text-xs text-[#059669] font-bold">Clean — no orphans remaining.</p>
                    ) : (
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-[#E5E5E5]">
                                    <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Name</th>
                                    <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Email</th>
                                    <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Source</th>
                                    <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Job ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orphans.orphans.map((c) => (
                                    <tr key={c.id} className="border-b border-[#F5F5F5]">
                                        <td className="p-2 text-ink truncate max-w-[150px]">{c.name || "—"}</td>
                                        <td className="p-2 text-[#525252] truncate max-w-[180px]">{c.email || "—"}</td>
                                        <td className="p-2 text-[#525252]">{c.source || "—"}</td>
                                        <td className="p-2 text-[10px] text-[#A3A3A3]">{(c.job_id || "—").slice(0, 8)}…</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>
            )}

            {/* V1.5.8.43 — Bulk Re-parse (AI extract phone/location/skills/... for all candidates that have resume_text but blank profile fields). */}
            <section
                data-testid="bulk-reparse-section"
                className="border border-[#E5E5E5] bg-white p-5 mt-8"
            >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div className="flex-1 min-w-[280px]">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0EA5E9] mb-1">
                            AI Bulk Re-parse
                        </p>
                        <h3 className="font-heading text-lg font-black tracking-tighter text-ink mb-1">
                            Fill blank profile fields from stored resumes
                        </h3>
                        <p className="text-xs text-[#525252] max-w-2xl">
                            Runs Claude Sonnet 4.5 on every candidate that has
                            <code className="mx-1 text-[10px] bg-[#F5F5F5] px-1.5 py-0.5">resume_text</code>
                            but is missing phone/location/current_title/skills/years_experience.
                            <strong className="font-black text-ink"> Fill-blanks-only</strong>
                            {" "}— recruiter edits are never overwritten.
                            Great for the 45k legacy candidate pool.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-end gap-3 mb-3">
                    <label className="flex flex-col text-[10px] font-black uppercase tracking-wider text-[#525252]">
                        Batch size
                        <input
                            type="number" min="1" max="200"
                            value={bulkLimit}
                            onChange={(e) => setBulkLimit(Math.max(1, Math.min(200, Number(e.target.value) || 50)))}
                            data-testid="bulk-reparse-limit"
                            className="mt-1 border border-[#E5E5E5] px-2 py-1.5 text-sm font-mono w-28"
                        />
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-xs text-[#525252] pb-1.5">
                        <input
                            type="checkbox" checked={bulkAllWs}
                            onChange={(e) => setBulkAllWs(e.target.checked)}
                            data-testid="bulk-reparse-all-ws"
                        />
                        All workspaces (superadmin)
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-xs text-[#525252] pb-1.5">
                        <input
                            type="checkbox" checked={bulkForce}
                            onChange={(e) => setBulkForce(e.target.checked)}
                            data-testid="bulk-reparse-force"
                        />
                        Force (re-process even filled rows — costs more LLM calls)
                    </label>
                    <button
                        type="button"
                        onClick={runBulkReparse}
                        disabled={busy !== null}
                        data-testid="bulk-reparse-run"
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider bg-[#0EA5E9] text-white hover:opacity-90 disabled:opacity-50"
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                        {busy === "bulk-reparse" ? "Running…" : "Run bulk re-parse"}
                    </button>
                </div>

                {bulkResult && (
                    <div className="mt-3 border-t border-[#F5F5F5] pt-3" data-testid="bulk-reparse-progress">
                        {/* V1.9.5 — Chunk timeline (WhatsApp-style status bar) */}
                        {(bulkResult.chunks_done != null || bulkResult.chunks) && (() => {
                            const done = bulkResult.chunks_done || (bulkResult.chunks || []).length || 0;
                            const expected = Math.max(1, Math.ceil((bulkResult.limit || done * 50) / 50));
                            const pct = Math.min(100, Math.round((done / expected) * 100));
                            const complete = bulkResult.complete;
                            return (
                                <div className="mb-3" data-testid="bulk-reparse-chunk-timeline">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[#525252]">
                                            {complete ? "All chunks done" : "Processing…"}
                                        </span>
                                        <span className="text-[11px] tabular-nums text-ink font-semibold">
                                            {done} / ~{expected} chunks
                                        </span>
                                    </div>
                                    <div className="h-2 rounded-full bg-[#F5F5F5] overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-500 ${
                                                complete ? "bg-emerald-500" : "bg-cobalt animate-pulse"
                                            }`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                    {Array.isArray(bulkResult.chunks) && bulkResult.chunks.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1" data-testid="bulk-reparse-chunk-list">
                                            {bulkResult.chunks.map((c, i) => {
                                                const hasError = c.totals?.errors > 0;
                                                const anyFilled = c.totals?.filled_any > 0;
                                                return (
                                                    <span
                                                        key={i}
                                                        title={`Rows ${c.offset}-${c.offset + c.size} · scanned ${c.totals?.scanned || 0} · filled ${c.totals?.filled_any || 0} · errors ${c.totals?.errors || 0}`}
                                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tabular-nums font-semibold border ${
                                                            hasError
                                                                ? "bg-red-50 border-red-200 text-red-700"
                                                                : anyFilled
                                                                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                                                    : "bg-[#F5F5F5] border-[#E5E5E5] text-[#525252]"
                                                        }`}
                                                    >
                                                        #{i + 1} · {c.size}
                                                        {hasError ? ` · ${c.totals.errors}e` : ""}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
                            <Stat label="Scanned" value={bulkResult.totals.scanned} tone="ink" />
                            <Stat label="Rows filled" value={bulkResult.totals.filled_any} tone="emerald" />
                            <Stat label="Fields filled" value={bulkResult.totals.fields_filled} tone="cobalt" />
                            <Stat label="No change" value={bulkResult.totals.filled_none} tone="ink" />
                            <Stat label="Errors" value={bulkResult.totals.errors} tone={bulkResult.totals.errors ? "warn" : "ink"} />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto border border-[#F5F5F5]">
                            <table className="w-full text-xs">
                                <thead className="bg-[#FAFAFA] sticky top-0">
                                    <tr>
                                        <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Candidate</th>
                                        <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Email</th>
                                        <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Fields filled</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkResult.results.map((r) => (
                                        <tr key={r.cid} className="border-b border-[#F5F5F5]">
                                            <td className="p-2 text-ink truncate max-w-[160px]">{r.name || "—"}</td>
                                            <td className="p-2 text-[#525252] truncate max-w-[200px]">{r.email || "—"}</td>
                                            <td className="p-2">
                                                {r.error ? (
                                                    <span className="text-red-600 font-mono text-[10px]">{r.error}</span>
                                                ) : (r.fields_filled?.length ? (
                                                    <span className="text-emerald-700">{r.fields_filled.join(", ")}</span>
                                                ) : (
                                                    <span className="text-[#A3A3A3]">— no change</span>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </section>

            {/* V1.5.8.44 — Legacy resume_url migration */}
            <section
                data-testid="resume-url-migration-section"
                className="border border-[#E5E5E5] bg-white p-5 mt-6"
            >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div className="flex-1 min-w-[280px]">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#F59E0B] mb-1">
                            Legacy URL cleanup
                        </p>
                        <h3 className="font-heading text-lg font-black tracking-tighter text-ink mb-1">
                            Normalise stale resume_url values
                        </h3>
                        <p className="text-xs text-[#525252] max-w-2xl">
                            Some legacy candidates carry
                            <code className="mx-1 text-[10px] bg-[#F5F5F5] px-1.5 py-0.5">resume_url</code>
                            values like <code className="text-[10px] bg-[#F5F5F5] px-1.5 py-0.5">"resume.pdf"</code>
                            {" "}or old session paths. Clicking Preview navigated the browser to
                            {" "}<code className="text-[10px] bg-[#F5F5F5] px-1.5 py-0.5">{"<origin>/resume.pdf"}</code>
                            {" "}→ FastAPI default 404 → JSON viewer instead of the PDF. This
                            job scans every row and either <strong className="font-black text-ink">rewrites</strong>
                            {" "}the URL to the canonical <code className="text-[10px] bg-[#F5F5F5] px-1.5 py-0.5">/api/storage/cv/{"{cid}"}</code>
                            {" "}path (when an R2 file exists) or <strong className="font-black text-ink">clears</strong> it (when nothing is on file).
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={previewResumeUrls}
                        disabled={busy !== null}
                        data-testid="url-mig-preview-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider border border-[#E5E5E5] bg-white text-ink hover:border-ink disabled:opacity-50"
                    >
                        <MagnifyingGlass size={14} weight="bold" />
                        {busy === "url-preview" ? "Scanning…" : "1. Preview malformed URLs"}
                    </button>
                    <button
                        type="button"
                        onClick={applyResumeUrls}
                        disabled={busy !== null || !urlPreview || urlPreview.malformed_count === 0}
                        data-testid="url-mig-apply-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider bg-[#F59E0B] text-white hover:opacity-90 disabled:opacity-40"
                    >
                        <Bandaids size={14} weight="bold" />
                        {busy === "url-apply" ? "Migrating…" : "2. Apply migration"}
                    </button>
                </div>

                {urlPreview && (
                    <div className="mt-3 border-t border-[#F5F5F5] pt-3">
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-2">
                            {urlPreview.malformed_count} malformed url(s) detected
                        </p>
                        {urlPreview.sample?.length > 0 && (
                            <div className="max-h-[200px] overflow-y-auto border border-[#F5F5F5]">
                                <table className="w-full text-xs">
                                    <thead className="bg-[#FAFAFA] sticky top-0">
                                        <tr>
                                            <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Name</th>
                                            <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Email</th>
                                            <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Current resume_url</th>
                                            <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">R2 key?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {urlPreview.sample.map((c) => (
                                            <tr key={c.id} className="border-b border-[#F5F5F5]">
                                                <td className="p-2 text-ink truncate max-w-[140px]">{c.name || "—"}</td>
                                                <td className="p-2 text-[#525252] truncate max-w-[180px]">{c.email || "—"}</td>
                                                <td className="p-2 text-[#525252] font-mono text-[10px] truncate max-w-[220px]">{c.resume_url || "—"}</td>
                                                <td className="p-2">
                                                    {c.resume_r2_key ? <span className="text-emerald-600">yes</span> : <span className="text-[#A3A3A3]">no</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {urlApplyResult && (
                    <div className="mt-3 border-t border-[#F5F5F5] pt-3">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            <Stat label="Scanned" value={urlApplyResult.totals.scanned} tone="ink" />
                            <Stat label="Normalised" value={urlApplyResult.totals.normalised} tone="emerald" />
                            <Stat label="Cleared" value={urlApplyResult.totals.cleared} tone="cobalt" />
                            <Stat label="Unchanged" value={urlApplyResult.totals.unchanged} tone="ink" />
                            <Stat label="Errors" value={urlApplyResult.totals.errors} tone={urlApplyResult.totals.errors ? "warn" : "ink"} />
                        </div>
                    </div>
                )}
            </section>

            {/* V1.5.8.45 — Orphan candidate users (a + c) */}
            <section
                data-testid="orphan-users-section"
                className="border border-[#E5E5E5] bg-white p-5 mt-6"
            >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div className="flex-1 min-w-[280px]">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#7E22CE] mb-1">
                            Orphan user reconcile
                        </p>
                        <h3 className="font-heading text-lg font-black tracking-tighter text-ink mb-1">
                            Users with auth accounts but no candidate row
                        </h3>
                        <p className="text-xs text-[#525252] max-w-2xl">
                            Every <code className="text-[10px] bg-[#F5F5F5] px-1.5 py-0.5">users(role=candidate)</code>
                            {" "}row should be referenced by at least one
                            <code className="mx-1 text-[10px] bg-[#F5F5F5] px-1.5 py-0.5">candidates.candidate_user_id</code>.
                            When it isn't, the user has an account but can't appear on any
                            recruiter's list — Admin dashboard "Registration Done" then under-reports
                            the true user base. Preview shows the count and per-source breakdown;
                            Apply either links to an existing row (by email match) or creates a
                            fresh Team-Pool row.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={previewOrphanUsers}
                        disabled={busy !== null}
                        data-testid="orphan-users-preview-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider border border-[#E5E5E5] bg-white text-ink hover:border-ink disabled:opacity-50"
                    >
                        <MagnifyingGlass size={14} weight="bold" />
                        {busy === "orphan-preview" ? "Scanning…" : "1. Preview orphans & breakdown"}
                    </button>
                    <button
                        type="button"
                        onClick={applyOrphanUsers}
                        disabled={busy !== null || !orphanUsers || (orphanUsers.orphan_count - (orphanUsers.breakdown?.test_data || 0)) === 0}
                        data-testid="orphan-users-apply-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider bg-[#7E22CE] text-white hover:opacity-90 disabled:opacity-40"
                    >
                        <Bandaids size={14} weight="bold" />
                        {busy === "orphan-apply" ? "Reconciling…" : "2. Reconcile orphans"}
                    </button>
                </div>

                {orphanUsers && (
                    <div className="mt-3 border-t border-[#F5F5F5] pt-3">
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-2">
                            {orphanUsers.orphan_count} orphan user(s) · Sources of the gap
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                            <Stat label="Test data (skip)" value={orphanUsers.breakdown?.test_data || 0} tone="ink" />
                            <Stat label="Resume Studio" value={orphanUsers.breakdown?.resume_studio || 0} tone="cobalt" />
                            <Stat label="Public-apply no link" value={orphanUsers.breakdown?.public_apply_no_link || 0} tone="warn" />
                            <Stat label="Self-signup no apply" value={orphanUsers.breakdown?.self_signup_no_apply || 0} tone="emerald" />
                        </div>
                        {orphanUsers.sample?.length > 0 && (
                            <div className="max-h-[260px] overflow-y-auto border border-[#F5F5F5]">
                                <table className="w-full text-xs">
                                    <thead className="bg-[#FAFAFA] sticky top-0">
                                        <tr>
                                            <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Email</th>
                                            <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Name</th>
                                            <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Source</th>
                                            <th className="text-left p-2 text-[10px] font-black uppercase tracking-wider text-[#525252]">Email match?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orphanUsers.sample.map((u) => (
                                            <tr key={u.id} className="border-b border-[#F5F5F5]">
                                                <td className="p-2 text-[#525252] truncate max-w-[220px]">{u.email || "—"}</td>
                                                <td className="p-2 text-ink truncate max-w-[140px]">{u.name || "—"}</td>
                                                <td className="p-2">
                                                    <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 ${
                                                        u.source === "test_data" ? "bg-[#F5F5F5] text-[#A3A3A3]" :
                                                        u.source === "resume_studio" ? "bg-cobalt/10 text-cobalt" :
                                                        u.source === "public_apply_no_link" ? "bg-[#FEF3C7] text-[#92400E]" :
                                                        "bg-emerald-50 text-emerald-700"
                                                    }`}>
                                                        {u.source}
                                                    </span>
                                                </td>
                                                <td className="p-2 text-[#525252]">
                                                    {u.candidates_by_email > 0
                                                        ? <span className="text-[#B91C1C] font-bold">{u.candidates_by_email} match</span>
                                                        : <span className="text-[#A3A3A3]">none</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {orphanApplyResult && (
                    <div className="mt-3 border-t border-[#F5F5F5] pt-3">
                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                            <Stat label="Scanned" value={orphanApplyResult.totals.scanned} tone="ink" />
                            <Stat label="Linked" value={orphanApplyResult.totals.linked_to_existing} tone="emerald" />
                            <Stat label="Created row" value={orphanApplyResult.totals.created_new_row} tone="cobalt" />
                            <Stat label="Skipped test" value={orphanApplyResult.totals.skipped_test_data} tone="ink" />
                            <Stat label="No email" value={orphanApplyResult.totals.skipped_no_email} tone="ink" />
                            <Stat label="Errors" value={orphanApplyResult.totals.errors} tone={orphanApplyResult.totals.errors ? "warn" : "ink"} />
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}

function Stat({ label, value, tone }) {
    const cls =
        tone === "ok"   ? "text-[#059669]" :
        tone === "warn" ? "text-[#B45309]" :
        tone === "muted"? "text-[#525252]" :
                          "text-ink";
    return (
        <div className="bg-[#FAFAFA] border border-[#F5F5F5] p-3">
            <p className="text-[9px] font-black uppercase tracking-wider text-[#A3A3A3] mb-1">{label}</p>
            <p className={`font-heading text-2xl font-black tracking-tighter tabular-nums ${cls}`}>{value ?? "—"}</p>
        </div>
    );
}

/** Directly hits the person 360 endpoint to prove whether it returns
 * data or an error for a specific candidate. Answers "why does the
 * drawer say 'No details available'?". */
function TestPerson360Btn({ cid }) {
    const [state, setState] = useState({ status: null, body: null, err: null });
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        setState({ status: null, body: null, err: null });
        try {
            const r = await api.get(`/candidates/${cid}/person`);
            setState({ status: r.status, body: r.data, err: null });
        } catch (e) {
            setState({
                status: e?.response?.status,
                body: e?.response?.data,
                err: e?.message,
            });
        } finally { setBusy(false); }
    };
    return (
        <div className="w-full">
            <button
                type="button"
                onClick={run}
                disabled={busy}
                data-testid={`heal-test-person360-${cid}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider border border-[#7E22CE] text-[#7E22CE] hover:bg-[#7E22CE]/5 disabled:opacity-50"
            >
                {busy ? "Testing…" : "Test person 360"}
            </button>
            {state.status !== null && (
                <div className={`mt-2 border p-2 text-[10px] ${
                    state.status >= 200 && state.status < 300
                        ? "border-[#059669] bg-[#ECFDF5] text-[#059669]"
                        : "border-[#B91C1C] bg-[#FEE2E2] text-[#B91C1C]"
                }`}>
                    <p className="font-black uppercase tracking-wider">HTTP {state.status}</p>
                    {state.body && (
                        <pre className="mt-1 overflow-x-auto text-[9px] text-[#525252] bg-white p-1.5">
{typeof state.body === "string" ? state.body : JSON.stringify(state.body, null, 2).slice(0, 800)}
                        </pre>
                    )}
                </div>
            )}
        </div>
    );
}

/** One-click backfill: extracts a plausible current_title from the
 * candidate's resume_text and saves it to the doc so cards no longer
 * show "—". */
function RepairTitleBtn({ cid, onDone }) {
    const [busy, setBusy] = useState(false);
    const run = async (manualTitle) => {
        setBusy(true);
        try {
            const r = await api.post(
                `/superadmin/heal/candidate/${cid}/repair-title`,
                manualTitle ? { title: manualTitle } : {},
            );
            if (r.data?.title) {
                toast.success(`Set title: "${r.data.title}" (from ${r.data.source})`);
                onDone && onDone();
            } else {
                // Auto-extract failed → ask the admin to type the title.
                const typed = window.prompt(
                    `Couldn't find a title in the resume or linked profile for this candidate.\n\nType the current role/title manually:`,
                    "",
                );
                if (typed && typed.trim()) {
                    setBusy(false);
                    return run(typed.trim());
                }
                toast(r.data?.detail || "Title not set.");
            }
        } catch (e) {
            toast.error(formatApiError(e) || "Repair failed.");
        } finally { setBusy(false); }
    };
    return (
        <button
            type="button"
            onClick={() => run(null)}
            disabled={busy}
            data-testid={`heal-repair-title-${cid}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider bg-[#059669] text-white hover:opacity-90 disabled:opacity-50"
        >
            {busy ? "Extracting…" : "Repair title from resume"}
        </button>
    );
}

/** V1.5.8.44 — one-click "Fix this row" that runs every safe fixup on
 * a single candidate: backfill workspace_id, flip is_team_pool, coerce
 * created_at, drop bad tags, normalise resume_url. Returns a diff which
 * we surface as a toast + expandable audit list. */
function FixRowBtn({ cid, onDone }) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const run = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/superadmin/heal/candidate/${cid}/fix-row`);
            setResult(r.data);
            if (r.data.changed) {
                const summary = (r.data.changes || [])
                    .map((c) => c.field).join(", ");
                toast.success(`Fixed ${r.data.changes.length} field(s): ${summary}`);
            } else {
                toast(r.data.skipped?.length
                    ? "Nothing to fix — row is already clean."
                    : "No changes applied.");
            }
            onDone && onDone();
        } catch (e) {
            toast.error(formatApiError(e) || "Fix failed.");
        } finally { setBusy(false); }
    };
    return (
        <div className="inline-flex flex-col items-start gap-1">
            <button
                type="button"
                onClick={run}
                disabled={busy}
                data-testid={`heal-fix-row-${cid}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider bg-[#0EA5E9] text-white hover:opacity-90 disabled:opacity-50"
            >
                <Bandaids size={11} weight="bold" />
                {busy ? "Fixing…" : "Fix this row"}
            </button>
            {result && (result.changes?.length > 0 || result.skipped?.length > 0) && (
                <details className="text-[10px] text-[#525252] pl-1">
                    <summary className="cursor-pointer font-bold uppercase tracking-wider">
                        {result.changed
                            ? `${result.changes.length} change${result.changes.length === 1 ? "" : "s"}`
                            : "audit"}
                    </summary>
                    <ul className="mt-1 space-y-0.5">
                        {(result.changes || []).map((ch, i) => (
                            <li key={`c${i}`} className="text-emerald-700">
                                ✓ {ch.field}: {ch.reason}
                            </li>
                        ))}
                        {(result.skipped || []).map((s, i) => (
                            <li key={`s${i}`} className="text-[#A3A3A3]">
                                — {s}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}

/** Sets is_team_pool=true so the row becomes visible to every
 * recruiter in the workspace. Only applicable when the row has no
 * owner (personal-scope rows are left alone). */
function RepairTeamPoolBtn({ cid, onDone }) {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/superadmin/heal/candidate/${cid}/repair-team-pool`);
            if (r.data?.changed) {
                toast.success("Moved to team pool — now visible to all recruiters.");
            } else {
                toast(r.data?.detail || "No change needed.");
            }
            onDone && onDone();
        } catch (e) {
            toast.error(formatApiError(e) || "Repair failed.");
        } finally { setBusy(false); }
    };
    return (
        <button
            type="button"
            onClick={run}
            disabled={busy}
            data-testid={`heal-repair-teampool-${cid}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider bg-[#0EA5E9] text-white hover:opacity-90 disabled:opacity-50"
        >
            {busy ? "Fixing…" : "Move to team pool"}
        </button>
    );
}
